import { Button } from '@tempo-io/sui-button';
import { Add, Close } from '@tempo-io/sui-icons';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyFieldValue } from '@/components/common/item-field-value/EmptyFieldValue';

export interface MultiValueFieldValue<T = string> {
  id?: number;
  value: T;
}

interface MultiValueFieldManagerProps<T = string> {
  values: MultiValueFieldValue<T>[];
  isEditable: boolean;
  disabled?: boolean;
  onRemove: (id: number) => void;
  maxCount?: number;
  initialIsAdding?: boolean;
  onAddSubmit?: () => void;
  onAddClick?: () => void;
  children: (item: MultiValueFieldValue<T>, index: number) => React.ReactNode;
  renderAddInput?: () => React.ReactNode;
}

export const MultiValueFieldManager = <T = string>({
  values,
  isEditable,
  disabled = false,
  onRemove,
  maxCount = 100,
  initialIsAdding = false,
  onAddSubmit,
  onAddClick,
  children,
  renderAddInput,
}: MultiValueFieldManagerProps<T>) => {
  const { t } = useTranslation();
  const [isAdding, setIsAdding] = useState(initialIsAdding);
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const canAddMore = values.length < maxCount;

  const handleAddClick = () => {
    onAddSubmit?.();
    setIsAdding(false);
    setTimeout(() => {
      addButtonRef.current?.focus();
    }, 0);
  };

  const handleCancel = () => {
    setIsAdding(false);
  };

  if (!isEditable) {
    if (values.length === 0) {
      return (
        <div className="text-sm text-text-neutral-primary">
          <EmptyFieldValue />
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-1">
        {values.map((item, index) => (
          <div
            key={item.id ?? index}
            className="text-sm text-text-neutral-primary"
            data-testid="multi-value-field-value"
          >
            {children(item, index)}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {values.length > 0 && (
        <div className="flex flex-col gap-2">
          {values.map((item, index) => (
            <div key={item.id ?? index} className="flex items-center gap-2">
              <div className="flex-1">{children(item, index)}</div>
              {item.id !== undefined && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemove(item.id as number)}
                  aria-label={t(
                    ($) => $.adaptivePlanner.itemPanel.customFields.multiValue.removeValue,
                  )}
                  data-testid="multi-value-remove-button"
                  className="h-10 w-10 p-0 hover:bg-bg-error-subtler-hovered hover:text-text-error-bold"
                  disabled={disabled}
                >
                  <Close />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {isAdding && renderAddInput && (
        <div className="flex items-center gap-2">
          <div className="flex-1">{renderAddInput()}</div>
          <Button
            variant="default"
            size="sm"
            onClick={handleAddClick}
            aria-label={t(($) => $.adaptivePlanner.itemPanel.customFields.multiValue.add)}
            data-testid="multi-value-add-button"
            className="h-8 px-3"
            disabled={disabled}
          >
            {t(($) => $.adaptivePlanner.itemPanel.customFields.multiValue.add)}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            aria-label={t(($) => $.adaptivePlanner.itemPanel.customFields.multiValue.cancel)}
            data-testid="multi-value-cancel-button"
            className="h-8 w-8 p-0 text-text-neutral-secondary"
            disabled={disabled}
          >
            <Close />
          </Button>
        </div>
      )}

      {!isAdding && canAddMore && (
        <Button
          ref={addButtonRef}
          variant="secondary"
          size="sm"
          data-testid="multi-value-add-value-button"
          onClick={() => (onAddClick ? onAddClick() : setIsAdding(true))}
          className="self-start gap-2"
          disabled={disabled}
        >
          <Add />
          {t(($) => $.adaptivePlanner.itemPanel.customFields.multiValue.addValue)}
        </Button>
      )}

      {!canAddMore && (
        <div
          className="text-xs text-text-neutral-secondary"
          data-testid="multi-value-max-count-message"
        >
          {t(($) => $.adaptivePlanner.itemPanel.customFields.validation.multiValueMaxCountReached, {
            maxCount,
          })}
        </div>
      )}
    </div>
  );
};
